import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid, IconButton, TableCell, TableRow } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import {
  type Amount,
  CurrencyTextField,
  PuiTooltip,
} from '@pbt/pbt-ui-components'
import { Edit as EditIcon } from '@pbt/pbt-ui-components/src/icons'

import PercentTextField from '~/components/common/inputs/PercentTextField'
import {
  addRange,
  formatMoneyRange,
  getDiscountedPrice,
  getExtendedPrice,
  getHighValue,
  getLimitedDiscountAmount,
  getLowValue,
  maxRange,
  minRange,
  subtractRange,
} from '~/components/dashboard/invoices/invoiceUtils'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { getCurrentBusinessIsOmniChannel } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { Bundle, BundleItem } from '~/types'
import useDialog from '~/utils/useDialog'

export const applyCustomBundlePriceDiscount =
  (discountPerc: number) =>
  (item = {} as BundleItem): BundleItem => ({
    ...item,
    discountAmount: null,
    discountPerc,
  })

// Presentational discount for fixed price bundles, backend also recalculates upon save
export const applyFixedBundlePriceDiscount = (
  item: BundleItem,
  discount: number,
) => ({
  ...item,
  discountAmount: discount,
  discountPerc: 0,
})

const getBundleItemHighPrice = (item: BundleItem) =>
  getHighValue(getExtendedPrice(item))

const useStyles = makeStyles(
  (theme) => ({
    tableCell: {
      color: theme.colors.secondaryText,
      fontSize: '1.4rem',
      height: 50,
      padding: theme.spacing(0, 1),
      borderBottom: 'none',
      fontWeight: 500,
    },
    lightText: {
      fontWeight: 400,
    },
    discountInput: {
      minWidth: 50,
      maxWidth: 65,
    },
    footerTableRow: {
      borderTop: theme.constants.tableBorder,
      backgroundColor: theme.colors.tableBackground,
    },
    editButton: {
      padding: theme.spacing(1),
    },
    editIcon: {
      color: theme.colors.editIconColor,
    },
  }),
  { name: 'BundleItemsTableFooter' },
)

export interface BundleItemsTableFooterProps {
  bundle: Bundle
  isPrePaidEnabled: boolean
  isRangeEnabled: boolean
  onBundleChange: (
    bundle: Bundle,
    shouldFetchDifferenceDistribution?: boolean,
  ) => void
}

const BundleItemsTableFooter = ({
  bundle,
  isRangeEnabled,
  isPrePaidEnabled,
  onBundleChange,
}: BundleItemsTableFooterProps) => {
  const classes = useStyles()
  const { t } = useTranslation('Admin')
  const isSetBundlePriceEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SET_BUNDLE_PRICE),
  )
  const isCurrentBusinessOmniChannel = useSelector(
    getCurrentBusinessIsOmniChannel,
  )
  const shouldUseSetBundlePrice =
    isSetBundlePriceEnabled && isCurrentBusinessOmniChannel

  const [discountPercent, setDiscountPercent] = useState<number | ''>('')

  const [openBundleCustomPriceDialog] = useDialog(
    DialogNames.BUNDLE_CUSTOM_PRICE,
  )
  const [openBundleFixedPriceDialog] = useDialog(DialogNames.BUNDLE_FIXED_PRICE)

  const bundleItems = bundle.items || []

  const bundlePrice = bundleItems.reduce(
    (acc: Amount, item: BundleItem) => addRange(acc, getExtendedPrice(item)),
    0,
  )
  const discountedBundlePrice = bundleItems.reduce(
    (acc: Amount, item: BundleItem) => addRange(acc, getDiscountedPrice(item)),
    0,
  )
  const itemsDiscount = bundleItems.reduce(
    (acc: Amount, item: BundleItem) =>
      addRange(acc, getLimitedDiscountAmount(item)),
    0,
  )
  const totalDiscount = minRange(
    addRange(itemsDiscount, bundle.additionalDiscount),
    bundlePrice,
  )
  const bundleTotalPrice =
    shouldUseSetBundlePrice && bundle.fixedPrice != null
      ? bundle.fixedPrice
      : maxRange(
          subtractRange(discountedBundlePrice, bundle.additionalDiscount),
          0,
        )

  const onDiscountAmountChange = (value: number) => {
    onBundleChange({ ...bundle, additionalDiscount: value })
  }

  const onDiscountPercChange = (value: number) => {
    onBundleChange({
      ...bundle,
      additionalDiscount: (getLowValue(discountedBundlePrice) * value) / 100,
    })
    setDiscountPercent(value)
  }

  // For clinics without fixed price feature
  const applyCustomPrice = (customBundlePrice: number) => {
    const oldBundlePrice = getHighValue(bundlePrice)
    const perItemDiscount = 1 - customBundlePrice / oldBundlePrice

    onBundleChange({
      ...bundle,
      items: bundleItems.map(applyCustomBundlePriceDiscount(perItemDiscount)),
      additionalDiscount: 0,
      fixedPrice: undefined,
    })
    setDiscountPercent('')
  }

  // For clinics with fixed price feature
  const applyFixedPrice = (
    fixedPrice: number,
    bundleItemDiscounts: number[],
  ) => {
    onBundleChange(
      {
        ...bundle,
        fixedPrice,
        additionalDiscount: 0,
        items: bundleItems.map((item, i) =>
          applyFixedBundlePriceDiscount(item, bundleItemDiscounts[i]),
        ),
      },
      false,
    )
  }

  const onBundlePriceEdit = () => {
    if (shouldUseSetBundlePrice) {
      openBundleFixedPriceDialog({
        onOk: applyFixedPrice,
        bundleItemHighPrices: bundleItems.map(getBundleItemHighPrice),
        extendedBundlePrice: bundlePrice,
      })
    } else {
      openBundleCustomPriceDialog({
        onOk: applyCustomPrice,
        extendedBundlePrice: bundlePrice,
      })
    }
  }

  const quantityOffset =
    isPrePaidEnabled && !isRangeEnabled ? 3 : isRangeEnabled ? 2 : 1
  const colOffset = 1 + quantityOffset

  const isBundlePriceEditable = useMemo(
    () => bundle?.items?.every((item) => item.price.discountAllowed),
    [bundle],
  )

  const showAdditionalDiscountInput =
    !shouldUseSetBundlePrice || Number(bundle.additionalDiscount) > 0

  return (
    <>
      {showAdditionalDiscountInput && (
        <TableRow className={classes.footerTableRow}>
          <TableCell className={classes.tableCell} colSpan={colOffset}>
            {t('Admin:CATALOG.BUNDLE_ITEMS_TABLE_FOOTER.ADDITIONAL_DISCOUNTS')}
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell className={classes.tableCell}>
            <PercentTextField
              className={classes.discountInput}
              data-testid="additional-discount-percent"
              disabled={shouldUseSetBundlePrice}
              placeholder="&nbsp;"
              value={discountPercent}
              onChange={onDiscountPercChange}
            />
          </TableCell>
          <TableCell className={classes.tableCell}>
            <CurrencyTextField
              className={classes.discountInput}
              disabled={shouldUseSetBundlePrice}
              value={bundle.additionalDiscount}
              onChange={(event) =>
                onDiscountAmountChange(Number(event.target.value.trim()))
              }
            />
          </TableCell>
          <TableCell
            className={classNames(classes.tableCell, classes.lightText)}
          >
            {formatMoneyRange(bundle.additionalDiscount, true)}
          </TableCell>
        </TableRow>
      )}
      <TableRow className={classes.footerTableRow}>
        <TableCell className={classes.tableCell} colSpan={colOffset}>
          {t('Admin:CATALOG.BUNDLE_ITEMS_TABLE_FOOTER.BUNDLE_TOTAL')}
        </TableCell>
        <TableCell />
        <TableCell className={classes.tableCell}>
          {formatMoneyRange(bundlePrice)}
        </TableCell>
        <TableCell />
        <TableCell className={classNames(classes.tableCell, classes.lightText)}>
          {formatMoneyRange(totalDiscount)}
        </TableCell>
        <TableCell className={classes.tableCell}>
          <Grid container alignItems="center" wrap="nowrap">
            {formatMoneyRange(bundleTotalPrice)}
            <PuiTooltip
              disabled={isBundlePriceEditable}
              tooltipPlacement="top-end"
              tooltipText={t(
                'Admin:CATALOG.BUNDLE_ITEMS_TABLE_FOOTER.EDIT_TOOLTIP',
              )}
            >
              <span>
                <IconButton
                  className={classes.editButton}
                  disabled={!isBundlePriceEditable}
                  onClick={onBundlePriceEdit}
                >
                  <EditIcon
                    className={classNames({
                      [classes.editIcon]: isBundlePriceEditable,
                    })}
                    fontSize="small"
                  />
                </IconButton>
              </span>
            </PuiTooltip>
          </Grid>
        </TableCell>
      </TableRow>
    </>
  )
}

export default BundleItemsTableFooter
